import logger from '@k/logger';
import urlExt from '@k/url';
import VisibilityObserver from '@k/visibility-observer';
import { DirectiveOptions } from 'vue';
import { DirectiveBinding } from 'vue/types/options';

const isDebug = Number(urlExt.getQuery('debug')) === 2;

function showOverlay(text: string, target: HTMLElement, type: number) {
  const newDiv = document.createElement('div');
  newDiv.className = `overlay ${type === 2 ? 'special' : 'normal'}`;
  newDiv.innerHTML = `<div class="text">${text}</div>`;
  target.className = target.className + ' overlay-wrapper';
  target.appendChild(newDiv);
}

function paramsParser(params: string) {
  let result;
  if (params.match(/&/) !== null) {
    result = urlExt.queryToJson(params);
  } else {
    result = { name: params };
  }
  return result;
}

function loggerPVHandler(value: string, el: HTMLElement) {
  const loggerContent = paramsParser(value);
  logger.send({
    type: 'pv',
    ...loggerContent
  });
  isDebug && showOverlay(value, el, 1);
}

function loggerClickHandler(target: MouseEvent, el: HTMLElement, value: string) {
  const loggerClickHits = 'loggerClickHits' in el.dataset ? el.dataset.loggerClickHits : '1';
  el.dataset.loggerClickHits = loggerClickHits;
  const loggerContent = paramsParser(value);
  logger.send({
    type: 'click',
    offsetX: target.offsetX,
    offsetY: target.offsetY,
    name: value,
    hits: loggerClickHits,
    ...loggerContent
  });
  isDebug && showOverlay(value, el, 2);
}

const loggerPV: DirectiveOptions = {
  bind: (el, { value, arg }, vnode) => {
    if (!value) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('[loggerPV] IntersectionObserver API is not available in your browser.');
    } else {
      (el as any)._kd_logger = new VisibilityObserver(el, arg || '', vnode, visible => {
        // eslint-disable-line
        visible && loggerPVHandler(value, el);
      });
    }
  },
  unbind: el => {
    const observer = (el as any)._kd_logger; // eslint-disable-line
    if (observer) {
      observer.destroy();
      delete (el as any)._kd_logger; // eslint-disable-line
    }
  },
  update: (el, { value, oldValue, arg }, vnode, oldVnode) => {
    if (value === oldValue) {
      return;
    }
    const observer = (el as any)._kd_logger; // eslint-disable-line
    if (!value) {
      loggerPV.unbind && loggerPV.unbind(el, {} as DirectiveBinding, vnode, oldVnode);
      return;
    }
    if (observer) {
      observer.create(el, vnode, () => {
        loggerPVHandler(value, el);
      });
    } else {
      loggerPV.bind && loggerPV.bind(el, { value, arg } as DirectiveBinding, vnode, oldVnode);
    }
  }
};
const loggerClick: DirectiveOptions = {
  bind: (el, { value }, vnode) => {
    vnode &&
      vnode.context &&
      vnode.context.$nextTick(() => {
        el.addEventListener(
          'click',
          target => {
            loggerClickHandler(target, el, value);
          },
          false
        );
      });
  },
  unbind: (el, { value }) => {
    const loggerClickHits = el.dataset.loggerClickHits;
    if (loggerClickHits) {
      el.removeEventListener(
        'click',
        target => {
          loggerClickHandler(target, el, value);
        },
        false
      );
    }
  },
  update: (el, { value, oldValue }, vnode, oldVnode) => {
    if (value === oldValue) {
      return;
    }
    const loggerClickHits = el.dataset.loggerClickHits;
    if (!value) {
      loggerClick.unbind && loggerClick.unbind(el, { value } as DirectiveBinding, vnode, oldVnode);
      return;
    }
    if (!loggerClickHits) {
      loggerClick.bind && loggerClick.bind(el, { value } as DirectiveBinding, vnode, oldVnode);
    }
  }
};

export default {
  loggerPV,
  loggerClick
};
